/**
 * Toolbar profissional da mesa (E37.F2): grupos semânticos com ícones // ux-neutrality:allow doc-comment
 * lucide + tooltip Radix, overflow responsivo e feedback por Toast. // ux-neutrality:allow doc-comment
 *
 * Grupos (esq. → dir.): // ux-neutrality:allow doc-comment
 *  Modo | Ferramentas (só Comparar) | Filtros | Zoom | [spacer] | Capturar CTA // ux-neutrality:allow doc-comment
 *
 * Quando a toolbar fica estreita (< TOOLBAR_NARROW_PX), Ferramentas e Filtros // ux-neutrality:allow doc-comment
 * colapsam num botão "···" (Radix DropdownMenu). // ux-neutrality:allow doc-comment
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Camera,
  Columns2,
  Grid3x3,
  Layers,
  MapPin,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Ruler,
  SunMoon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import type { AnnotationUiState } from './AnnotationTools';
import { ToolsGroup } from './ContestedToolsGroup';
import { ToolBtn } from './ToolBtn';
import type { InspectionFiltersState } from './ContestedInspectionFilters';
import type { ZoomCommand } from './ZoomPanViewport';
import { ZoomToolbarControls } from './ZoomToolbarControls';

export const TOOLBAR_NARROW_PX = 520;

type Mode = 'compare' | 'overlap';

// ---- FiltersGroup: Filtros (sempre visível) ----

interface FiltersGroupProps {
  filters: InspectionFiltersState;
  onToggleFilter: (key: keyof InspectionFiltersState) => void;
  rulerAvailable: boolean;
  showMarkersButton: boolean;
  showMarkers: boolean;
  onToggleMarkers: () => void;
  prefix?: string;
}

function FiltersGroup({
  filters,
  onToggleFilter,
  rulerAvailable,
  showMarkersButton,
  showMarkers,
  onToggleMarkers,
  prefix = '',
}: FiltersGroupProps): JSX.Element {
  return (
    <div className="toolbar-group" role="group" aria-label={ptBR.workspaceCaso.filtersGroupLabel}>
      <ToolBtn
        testId={`${prefix}inspect-invert`}
        icon={<SunMoon className="toolbar-icon" />}
        label={ptBR.workspaceCaso.inspectInvert}
        pressed={filters.invert}
        onClick={() => onToggleFilter('invert')}
      />
      <ToolBtn
        testId={`${prefix}inspect-grid`}
        icon={<Grid3x3 className="toolbar-icon" />}
        label={ptBR.workspaceCaso.inspectGrid}
        pressed={filters.grid}
        onClick={() => onToggleFilter('grid')}
      />
      <ToolBtn
        testId={`${prefix}inspect-ruler`}
        icon={<Ruler className="toolbar-icon" />}
        label={ptBR.workspaceCaso.inspectRuler}
        {...(!rulerAvailable && { hint: ptBR.workspaceCaso.inspectRulerCompareOnly })}
        pressed={rulerAvailable && filters.ruler}
        disabled={!rulerAvailable}
        onClick={() => onToggleFilter('ruler')}
      />
      {showMarkersButton && (
        <ToolBtn
          testId={`${prefix}markers-toggle`}
          icon={<MapPin className="toolbar-icon" />}
          label={showMarkers ? ptBR.workspaceCaso.markersHide : ptBR.workspaceCaso.markersShow}
          pressed={showMarkers}
          onClick={onToggleMarkers}
        />
      )}
    </div>
  );
}

// ---- OverflowMenu: grupos colapsados ----

interface OverflowMenuProps {
  mode: Mode;
  annotationUi: AnnotationUiState | null;
  /** E47.F3: a seleção é uma sugestão da análise preliminar? */
  selectedIsTriagem: boolean;
  onAdotar: (id: string) => void;
  filters: InspectionFiltersState;
  onToggleFilter: (key: keyof InspectionFiltersState) => void;
  rulerAvailable: boolean;
  showMarkersButton: boolean;
  showMarkers: boolean;
  onToggleMarkers: () => void;
}

function OverflowMenu({
  mode,
  annotationUi,
  selectedIsTriagem,
  onAdotar,
  filters,
  onToggleFilter,
  rulerAvailable,
  showMarkersButton,
  showMarkers,
  onToggleMarkers,
}: OverflowMenuProps): JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="toolbar-btn"
          data-testid="toolbar-overflow-btn"
          aria-label={ptBR.workspaceCaso.toolbarOverflow}
        >
          <MoreHorizontal className="toolbar-icon" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="zoom-menu-content" align="start" sideOffset={4}>
          {mode === 'compare' && annotationUi !== null && (
            <ToolsGroup
              annotationUi={annotationUi}
              prefix="overflow-"
              selectedIsTriagem={selectedIsTriagem}
              onAdotar={onAdotar}
            />
          )}
          <FiltersGroup
            filters={filters}
            onToggleFilter={onToggleFilter}
            rulerAvailable={rulerAvailable}
            showMarkersButton={showMarkersButton}
            showMarkers={showMarkers}
            onToggleMarkers={onToggleMarkers}
            prefix="overflow-"
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// ---- ContestedToolbar ----

export interface ContestedToolbarProps {
  mode: Mode;
  onMode: (mode: Mode) => void;
  filters: InspectionFiltersState;
  onToggleFilter: (key: keyof InspectionFiltersState) => void;
  rulerAvailable: boolean;
  /** null no modo Sobrepor & alinhar (ferramentas de anotação inativas). */
  annotationUi: AnnotationUiState | null;
  /** E47.F3: a seleção é uma sugestão da análise preliminar? */
  selectedIsTriagem: boolean;
  onAdotar: (id: string) => void;
  showMarkersButton: boolean;
  showMarkers: boolean;
  onToggleMarkers: () => void;
  onZoomCommand: (action: ZoomCommand['action'], percent?: number) => void;
  syncPanels: boolean;
  onToggleSync: () => void;
  onCapture: (() => void) | null;
  captureBusy: boolean;
  /** E37.F3.T03: modo foco — recolhe as colunas laterais. Ausente = sem botão. */
  focusMode?: boolean;
  onToggleFocus?: (() => void) | null;
}

export function ContestedToolbar({
  mode,
  onMode,
  filters,
  onToggleFilter,
  rulerAvailable,
  annotationUi,
  selectedIsTriagem,
  onAdotar,
  showMarkersButton,
  showMarkers,
  onToggleMarkers,
  onZoomCommand,
  syncPanels,
  onToggleSync,
  onCapture,
  captureBusy,
  focusMode = false,
  onToggleFocus = null,
}: ContestedToolbarProps): JSX.Element {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setIsNarrow((entry?.contentRect.width ?? 0) < TOOLBAR_NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const inCompare = mode === 'compare';

  return (
    <div
      ref={toolbarRef}
      className="contested-toolbar"
      role="toolbar"
      aria-label={ptBR.workspaceCaso.toolbarLabel}
    >
      {/* Grupo Modo */}
      <div className="toolbar-group" role="group" aria-label={ptBR.workspaceCaso.modeGroupLabel}>
        <ToolBtn
          testId="tool-compare"
          icon={<Columns2 className="toolbar-icon" />}
          label={ptBR.workspaceCaso.toolCompare}
          pressed={inCompare}
          onClick={() => onMode('compare')}
        />
        <ToolBtn
          testId="tool-overlap"
          icon={<Layers className="toolbar-icon" />}
          label={ptBR.workspaceCaso.toolOverlap}
          pressed={!inCompare}
          onClick={() => onMode('overlap')}
        />
      </div>

      <span className="inspection-divider" aria-hidden="true" />

      {/* Grupos colapsáveis */}
      {isNarrow ? (
        <OverflowMenu
          mode={mode}
          annotationUi={annotationUi}
          selectedIsTriagem={selectedIsTriagem}
          onAdotar={onAdotar}
          filters={filters}
          onToggleFilter={onToggleFilter}
          rulerAvailable={rulerAvailable}
          showMarkersButton={showMarkersButton}
          showMarkers={showMarkers}
          onToggleMarkers={onToggleMarkers}
        />
      ) : (
        <>
          {inCompare && annotationUi !== null && (
            <>
              <ToolsGroup
                annotationUi={annotationUi}
                selectedIsTriagem={selectedIsTriagem}
                onAdotar={onAdotar}
              />
              <span className="inspection-divider" aria-hidden="true" />
            </>
          )}
          <FiltersGroup
            filters={filters}
            onToggleFilter={onToggleFilter}
            rulerAvailable={rulerAvailable}
            showMarkersButton={showMarkersButton}
            showMarkers={showMarkers}
            onToggleMarkers={onToggleMarkers}
          />
          <span className="inspection-divider" aria-hidden="true" />
        </>
      )}

      {/* Grupo Zoom */}
      <ZoomToolbarControls
        onCommand={onZoomCommand}
        syncAvailable={inCompare}
        syncPanels={syncPanels}
        onToggleSync={onToggleSync}
      />

      {/* Spacer + Modo foco + Capturar CTA */}
      <div className="toolbar-spacer" aria-hidden="true" />
      {onToggleFocus !== null && (
        <ToolBtn
          testId="focus-toggle"
          icon={
            focusMode ? (
              <Minimize2 className="toolbar-icon" />
            ) : (
              <Maximize2 className="toolbar-icon" />
            )
          }
          label={focusMode ? ptBR.workspaceCaso.focusExit : ptBR.workspaceCaso.focusEnter}
          hint={ptBR.workspaceCaso.focusGestureTitle}
          pressed={focusMode}
          onClick={onToggleFocus}
        />
      )}
      {onCapture !== null && (
        <ToolBtn
          testId="capture-to-laudo"
          icon={<Camera className="toolbar-icon" />}
          label={ptBR.workspaceCaso.captureToLaudo}
          hint={ptBR.workspaceCaso.captureGestureTitle}
          disabled={captureBusy}
          primary
          onClick={onCapture}
        />
      )}
    </div>
  );
}
